import {
    ReactElement,
    useEffect,
} from 'react';

import {
    SimpleGrid,
    Table,
} from '@mantine/core';

import {useProducts} from '../hooks/useProducts';
import {PaginationControls} from '../../../components/PaginationControls';

import {
    RowSelectionDataCell,
    SimpleTableBatchAction,
    TableBody,
    TableBodyColumn,
    TableHeaderColumn,
    TableHeaderRow,
} from '@flexinet/ui-components';
import {IProductTableColumn, Product,} from '../types';
import {useVerifiedPermissions} from "../../../hooks/useVerifiedPermissions.ts";
import {rbacActions} from "../../../config/rbac/actions.ts";
import {getColumns} from '../tables/columns';

interface RecordsTableProps {
    selectedRecords?: Product[];
    setSelectedRecords?: (records: Product[]) => void;
    extraTools?: ReactElement[];
    settings?: { label: string, onClick: () => void }[];
}

export const RecordsTable = ({
                                 selectedRecords = [],
                                 setSelectedRecords,
                                 extraTools,
                                 settings,
                             }: RecordsTableProps) => {

    const {allowedActions} = useVerifiedPermissions()

    const {
        visibleItems,
        loading,
        fetchRecords,
        handleFilterByKeyword,
        currentPage,
        hasNext,
        hasPrevious,
        goToNextPage,
        goToPreviousPage,
    } = useProducts();

    useEffect(() => {
        fetchRecords();
    }, []);

    const selection: Record<string, Product> = {};
    for (const record of selectedRecords) {
        selection[record.id] = record;
    }

    const handleRowSelection = (row: { id: string }) => {
        if (!setSelectedRecords) return;
        if (selection[row.id]) {
            setSelectedRecords(selectedRecords.filter(r => r.id !== row.id));
        } else {
            const fullRecord = visibleItems.find(item => item.id === row.id);
            if (fullRecord) {
                setSelectedRecords([...selectedRecords, fullRecord]);
            }
        }
    };

    const handleSelectAllRows = () => {
        if (!setSelectedRecords) return;
        if (selectedRecords.length === visibleItems.length) {
            setSelectedRecords([]);
        } else {
            setSelectedRecords([...visibleItems]);
        }
    };

    const tableColumns = getColumns();

    const getColumnsWithSelection = () => {
        let cols: IProductTableColumn[] = [];
        if (allowedActions.includes(rbacActions.DeleteProduct)) {
            cols.push({
                id: 'select',
                title: '',
                render: item => {
                    return <RowSelectionDataCell item={item} handleChange={handleRowSelection}/>;
                },
            });
        }

        cols = cols.concat(tableColumns);
        return cols;
    };


    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <SimpleTableBatchAction
                limit={10}
                handleItemsPerPageChange={() => {}}
                handleFilterByKeyword={handleFilterByKeyword}
                settings={settings}
                extraTools={extraTools}
            />
            <Table verticalSpacing='sm' striped highlightOnHover>
                <TableHeaderRow
                    columns={getColumnsWithSelection() as TableHeaderColumn[]}
                    handleSelectAllRows={handleSelectAllRows}
                />
                <TableBody
                    items={visibleItems}
                    loading={loading}
                    selection={selection}
                    columns={getColumnsWithSelection() as TableBodyColumn[]}
                />
            </Table>
            <PaginationControls
                currentPage={currentPage}
                hasNext={hasNext}
                hasPrevious={hasPrevious}
                onNext={goToNextPage}
                onPrevious={goToPreviousPage}
                loading={loading}
            />
        </SimpleGrid>
    );
};
